import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Content from '../../../layouts/adminLayouts/Content/Content';
import Actions from '../../../layouts/adminLayouts/Actions/Actions';
import { getCsrfToken } from '../../../../utils/helpers';
import './crptPage.scss';

type SyncStatus = 'success' | 'partial' | 'failed' | 'processing' | 'unknown';
type CrptSyncRecord = {
    syncId: string;
    syncedAt: string; // ISO
    status: SyncStatus;
    rowsRead?: number;
    imported?: number;
    updated?: number;
    invalid?: number;
    duplicates?: number;
    sourceFileName?: string;
    elapsedMs?: number;
};
type CrptHistoryResponse = {
    ok: true;
    items: CrptSyncRecord[];
};
type CrptTotals = {
    total: number;
    available: number;
    reserved: number;
    used: number;
    invalid: number;
};
type CrptStatsResponse = {
    ok: true;
    updatedAt: string; // ISO
    totals: CrptTotals;
    lastSync?: Pick<CrptSyncRecord, 'syncId' | 'syncedAt' | 'status'>;
};
type CrptImportResponse = {
    ok: true;
    record: CrptSyncRecord;
};
type CrptSearchItem = {
    id: string;
    article?: string;
    country?: string;
    barcode?: string;
    codeShort?: string;
    codeFull?: string;
    status?: string;
    state?: string;
    producedAt?: string;
};
type CrptSearchResponse = {
    ok: true;
    total: number;
    items: CrptSearchItem[];
};
type ImportMode = 'upsert' | 'replace';
type UiStatus = 'idle' | 'loading' | 'success' | 'error';
type CrptSyncProductsResponse = {
    ok: true;
    updatedAt: string;
    totals: {
        totalProducts: number;
        updatedProducts: number;
        matchedProducts: number;
        uniqueArticles: number;
        totalAvailableCodes: number;
        elapsedMs: number;
    };
};

const API_BASE = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
const MAX_XLSX_BYTES = 15 * 1024 * 1024; // 15MB (как на бэке)

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const getStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const getNum = (v: unknown, def = 0): number => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isFinite(n) ? n : def;
};
const getArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

const readOptionalNumber = (v: unknown): number | undefined => {
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
    return Number.isFinite(n) ? n : undefined;
};
const readOptionalString = (v: unknown): string | undefined => {
    const s = getStr(v).trim();
    return s.length > 0 ? s : undefined;
};
const safeStatus = (v: unknown): SyncStatus => {
    const s = getStr(v).trim().toLowerCase();
    if (s === 'success') return 'success';
    if (s === 'partial') return 'partial';
    if (s === 'failed') return 'failed';
    if (s === 'processing') return 'processing';
    return 'unknown';
};
const normalizeScan = (v: string): string => {
    const s = String(v ?? '')
        .replace(/\r?\n/g, '')
        .trim();
    return s.startsWith(']d2') ? s.slice(3) : s;
};
// мягкая проверка (не валидируем формат ЧЗ строго)
const looksLikeChz = (v: string): boolean => {
    const s = normalizeScan(v);
    if (!s) return false;
    if (s.length < 10 || s.length > 512) return false;
    return /\d/.test(s);
};
const formatBytes = (bytes: number): string => {
    const b = Math.max(0, bytes);
    if (b < 1024) return `${b} B`;
    const kb = b / 1024;
    if (kb < 1024) return `${Math.round(kb * 10) / 10} KB`;
    const mb = kb / 1024;
    return `${Math.round(mb * 10) / 10} MB`;
};
const formatDateTimeRu = (iso: string): string => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};
const statusLabel = (s: SyncStatus): string => {
    switch (s) {
        case 'success':
            return 'Успешно';
        case 'partial':
            return 'Частично';
        case 'failed':
            return 'Ошибка';
        case 'processing':
            return 'В обработке';
        default:
            return 'Неизвестно';
    }
};
const codeStatusLabel = (s?: string): string | '' => {
    switch ((s || '').toLowerCase()) {
        case 'available':
            return 'Доступен';
        case 'reserved':
            return 'Зарезервирован';
        case 'used':
            return 'Использован';
        case 'invalid':
            return 'Ошибочный';
        default:
            return '';
    }
};
const parseApiError = (raw: unknown): string | null => {
    if (!isRecord(raw)) return null;
    const e = readOptionalString(raw.error);
    if (e) return e;
    const m = readOptionalString(raw.message);
    if (m) return m;
    return null;
};
const parseStatsResponse = (raw: unknown): CrptStatsResponse | null => {
    if (!isRecord(raw)) return null;
    if (raw.ok !== true) return null;
    const updatedAt = getStr(raw.updatedAt).trim();
    const totalsRaw = isRecord(raw.totals) ? raw.totals : null;
    if (!updatedAt || !totalsRaw) return null;
    const totals: CrptTotals = {
        total: clamp(getNum(totalsRaw.total, 0), 0, 1_000_000_000),
        available: clamp(getNum(totalsRaw.available, 0), 0, 1_000_000_000),
        reserved: clamp(getNum(totalsRaw.reserved, 0), 0, 1_000_000_000),
        used: clamp(getNum(totalsRaw.used, 0), 0, 1_000_000_000),
        invalid: clamp(getNum(totalsRaw.invalid, 0), 0, 1_000_000_000),
    };
    let lastSync: CrptStatsResponse['lastSync'] | undefined;
    if (isRecord(raw.lastSync)) {
        const syncId = getStr(raw.lastSync.syncId).trim();
        const syncedAt = getStr(raw.lastSync.syncedAt).trim();
        if (syncId && syncedAt) {
            lastSync = { syncId, syncedAt, status: safeStatus(raw.lastSync.status) };
        }
    }
    return { ok: true, updatedAt, totals, ...(lastSync ? { lastSync } : {}) };
};
const parseHistoryResponse = (raw: unknown): CrptHistoryResponse | null => {
    if (!isRecord(raw)) return null;
    if (raw.ok !== true) return null;
    const items = getArr(raw.items)
        .map((x): CrptSyncRecord | null => {
            if (!isRecord(x)) return null;
            const syncId = getStr(x.syncId).trim();
            const syncedAt = getStr(x.syncedAt).trim();
            if (!syncId || !syncedAt) return null;
            const rowsRead = readOptionalNumber(x.rowsRead);
            const imported = readOptionalNumber(x.imported);
            const updated = readOptionalNumber(x.updated);
            const invalid = readOptionalNumber(x.invalid);
            const duplicates = readOptionalNumber(x.duplicates);
            const sourceFileName = readOptionalString(x.sourceFileName);
            const elapsedMs = readOptionalNumber(x.elapsedMs);
            const rec: CrptSyncRecord = {
                syncId,
                syncedAt,
                status: safeStatus(x.status),
                ...(rowsRead !== undefined ? { rowsRead } : {}),
                ...(imported !== undefined ? { imported } : {}),
                ...(updated !== undefined ? { updated } : {}),
                ...(invalid !== undefined ? { invalid } : {}),
                ...(duplicates !== undefined ? { duplicates } : {}),
                ...(sourceFileName !== undefined ? { sourceFileName } : {}),
                ...(elapsedMs !== undefined ? { elapsedMs } : {}),
            };
            return rec;
        })
        .filter((x): x is CrptSyncRecord => x !== null);
    return { ok: true, items };
};
const parseSearchResponse = (raw: unknown): CrptSearchResponse | null => {
    if (!isRecord(raw)) return null;
    if (raw.ok !== true) return null;
    const total = clamp(getNum(raw.total, 0), 0, 1_000_000_000);
    const items = getArr(raw.items)
        .map((x): CrptSearchItem | null => {
            if (!isRecord(x)) return null;
            const id = getStr(x.id).trim();
            if (!id) return null;
            const article = readOptionalString(x.article);
            const country = readOptionalString(x.country);
            const barcode = readOptionalString(x.barcode);
            const codeShort = readOptionalString(x.codeShort ?? x.code_short);
            const codeFull = readOptionalString(x.codeFull ?? x.code_full);
            const status = readOptionalString(x.status);
            const state = readOptionalString(x.state);
            const producedAt = readOptionalString(x.producedAt);
            const rec: CrptSearchItem = {
                id,
                ...(article !== undefined ? { article } : {}),
                ...(country !== undefined ? { country } : {}),
                ...(barcode !== undefined ? { barcode } : {}),
                ...(codeShort !== undefined ? { codeShort } : {}),
                ...(codeFull !== undefined ? { codeFull } : {}),
                ...(status !== undefined ? { status } : {}),
                ...(state !== undefined ? { state } : {}),
                ...(producedAt !== undefined ? { producedAt } : {}),
            };
            return rec;
        })
        .filter((x): x is CrptSearchItem => x !== null);
    return { ok: true, total, items };
};

const parseImportResponse = (raw: unknown): CrptImportResponse | null => {
    if (!isRecord(raw)) return null;
    if (raw.ok !== true) return null;
    if (!isRecord(raw.record)) return null;
    const r = raw.record;
    const syncId = getStr(r.syncId).trim();
    const syncedAt = getStr(r.syncedAt).trim();
    if (!syncId || !syncedAt) return null;
    const rowsRead = readOptionalNumber(r.rowsRead);
    const imported = readOptionalNumber(r.imported);
    const updated = readOptionalNumber(r.updated);
    const invalid = readOptionalNumber(r.invalid);
    const duplicates = readOptionalNumber(r.duplicates);
    const sourceFileName = readOptionalString(r.sourceFileName);
    const elapsedMs = readOptionalNumber(r.elapsedMs);
    const record: CrptSyncRecord = {
        syncId,
        syncedAt,
        status: safeStatus(r.status),
        ...(rowsRead !== undefined ? { rowsRead } : {}),
        ...(imported !== undefined ? { imported } : {}),
        ...(updated !== undefined ? { updated } : {}),
        ...(invalid !== undefined ? { invalid } : {}),
        ...(duplicates !== undefined ? { duplicates } : {}),
        ...(sourceFileName !== undefined ? { sourceFileName } : {}),
        ...(elapsedMs !== undefined ? { elapsedMs } : {}),
    };
    return { ok: true, record };
};

const parseSyncProductsResponse = (raw: unknown): CrptSyncProductsResponse | null => {
    if (!isRecord(raw)) return null;
    if (raw.ok !== true) return null;
    const updatedAt = getStr(raw.updatedAt).trim();
    if (!updatedAt) return null;
    if (!isRecord(raw.totals)) return null;
    const t = raw.totals;
    const totals = {
        totalProducts: clamp(getNum(t.totalProducts, 0), 0, 1_000_000_000),
        updatedProducts: clamp(getNum(t.updatedProducts, 0), 0, 1_000_000_000),
        matchedProducts: clamp(getNum(t.matchedProducts, 0), 0, 1_000_000_000),
        uniqueArticles: clamp(getNum(t.uniqueArticles, 0), 0, 1_000_000_000),
        totalAvailableCodes: clamp(getNum(t.totalAvailableCodes, 0), 0, 2_000_000_000),
        elapsedMs: clamp(getNum(t.elapsedMs, 0), 0, 10_000_000),
    };
    return { ok: true, updatedAt, totals };
};

const CrptPage: React.FC = () => {
    // Upload
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [importMode, setImportMode] = useState<ImportMode>('upsert');
    const [dryRun, setDryRun] = useState<boolean>(false);
    const [uploadStatus, setUploadStatus] = useState<UiStatus>('idle');
    const [uploadError, setUploadError] = useState<string>('');
    const [importResult, setImportResult] = useState<CrptSyncRecord | null>(null);
    // Stats + history
    const [statsStatus, setStatsStatus] = useState<UiStatus>('idle');
    const [statsError, setStatsError] = useState<string>('');
    const [statsHttpStatus, setStatsHttpStatus] = useState<number | null>(null);
    const [stats, setStats] = useState<CrptStatsResponse | null>(null);
    const [historyStatus, setHistoryStatus] = useState<UiStatus>('idle');
    const [historyError, setHistoryError] = useState<string>('');
    const [historyHttpStatus, setHistoryHttpStatus] = useState<number | null>(null);
    const [history, setHistory] = useState<CrptSyncRecord[]>([]);
    // Search
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchPage, setSearchPage] = useState<number>(0);
    const [searchLimit, setSearchLimit] = useState<number>(20);
    const [searchStatus, setSearchStatus] = useState<UiStatus>('idle');
    const [searchError, setSearchError] = useState<string>('');
    const [searchTotal, setSearchTotal] = useState<number>(0);
    const [searchItems, setSearchItems] = useState<CrptSearchItem[]>([]);
    // Tool: code checker
    const [codeInput, setCodeInput] = useState<string>('');
    // Sync products (маркировка -> товары)
    const [syncStatus, setSyncStatus] = useState<UiStatus>('idle');
    const [syncError, setSyncError] = useState<string>('');
    const [syncResult, setSyncResult] = useState<CrptSyncProductsResponse | null>(null);
    const codeNormalized = useMemo(() => normalizeScan(codeInput), [codeInput]);
    const codeIsOk = useMemo(() => (codeNormalized ? looksLikeChz(codeNormalized) : false), [codeNormalized]);
    const fileMeta = useMemo(() => {
        if (!file) return null;
        return {
            name: file.name,
            sizeLabel: formatBytes(file.size),
            lastModified: new Date(file.lastModified).toLocaleString('ru-RU'),
        };
    }, [file]);
    const backendHint = useMemo(() => {
        return {
            import: '/crpt/import-xlsx',
            stats: '/crpt/stats',
            history: '/crpt/history?limit=10',
            search: '/crpt/search',
            searchHint: '/crpt/search?q=...&skip=...&limit=...',
            syncProducts: '/crpt/sync-products',
        };
    }, []);

    const validateXlsxFile = (f: File): string | null => {
        const name = String(f.name || '').trim();
        const lower = name.toLowerCase();
        if (!lower.endsWith('.xlsx')) return 'Нужен файл формата .xlsx';
        if (f.size <= 0) return 'Файл пустой';
        if (f.size > MAX_XLSX_BYTES) return `Файл слишком большой. Лимит: ${formatBytes(MAX_XLSX_BYTES)}`;
        return null;
    };

    const pickFile = (f: File | null) => {
        setUploadError('');
        setImportResult(null);
        if (!f) {
            setFile(null);
            return;
        }
        const err = validateXlsxFile(f);
        if (err) {
            setFile(null);
            setUploadError(err);
            setUploadStatus('error');
            return;
        }
        setFile(f);
        setUploadStatus('idle');
    };

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        pickFile(f);
        // позволяем выбрать тот же файл повторно
        e.target.value = '';
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const dt = e.dataTransfer;
        const f = dt?.files && dt.files.length ? dt.files[0] : null;
        pickFile(f);
    };

    const fetchJsonTry = useCallback(async (url: string, init: RequestInit, withCsrf: boolean, signal?: AbortSignal) => {
        const headers: HeadersInit = { 'X-Requested-With': 'XMLHttpRequest', ...(init.headers || {}) };
        if (withCsrf) {
            try {
                const csrf = await getCsrfToken();
                (headers as Record<string, string>)['csrf-token'] = csrf;
            } catch {
                // ignore
            }
        }
        const resp = await fetch(url, { ...init, headers, credentials: 'include', signal });
        const data: unknown = await resp.json().catch(() => null);
        return { resp, data };
    }, []);

    const fetchJsonSmartGet = useCallback(
        async (url: string, signal?: AbortSignal) => {
            let x = await fetchJsonTry(url, { method: 'GET' }, false, signal);
            if (x.resp.status === 401 || x.resp.status === 403) {
                x = await fetchJsonTry(url, { method: 'GET' }, true, signal);
            }
            return x;
        },
        [fetchJsonTry],
    );

    const loadStats = useCallback(
        async (signal?: AbortSignal) => {
            setStatsStatus('loading');
            setStatsError('');
            try {
                const { resp, data } = await fetchJsonSmartGet(`${API_BASE}${backendHint.stats}`, signal);
                setStatsHttpStatus(resp.status);
                if (!resp.ok) {
                    const msg = parseApiError(data) || `Ошибка загрузки сводки: ${resp.status} ${resp.statusText}`;
                    throw new Error(msg);
                }
                const parsed = parseStatsResponse(data);
                if (!parsed) throw new Error('Некорректный формат ответа сводки (ожидается { ok:true, ... })');
                setStats(parsed);
                setStatsStatus('success');
            } catch (e) {
                if (signal?.aborted) return;
                setStats(null);
                setStatsStatus('error');
                setStatsError(e instanceof Error ? e.message : 'Неизвестная ошибка загрузки сводки');
                setStatsHttpStatus(null);
            }
        },
        [backendHint.stats, fetchJsonSmartGet],
    );

    const loadHistory = useCallback(
        async (signal?: AbortSignal) => {
            setHistoryStatus('loading');
            setHistoryError('');
            try {
                const { resp, data } = await fetchJsonSmartGet(`${API_BASE}${backendHint.history}`, signal);
                setHistoryHttpStatus(resp.status);
                if (!resp.ok) {
                    const msg = parseApiError(data) || `Ошибка загрузки истории: ${resp.status} ${resp.statusText}`;
                    throw new Error(msg);
                }
                const parsed = parseHistoryResponse(data);
                if (!parsed) throw new Error('Некорректный формат ответа истории (ожидается { ok:true, items:[] })');
                setHistory(parsed.items);
                setHistoryStatus('success');
            } catch (e) {
                if (signal?.aborted) return;
                setHistory([]);
                setHistoryStatus('error');
                setHistoryError(e instanceof Error ? e.message : 'Неизвестная ошибка загрузки истории');
                setHistoryHttpStatus(null);
            }
        },
        [backendHint.history, fetchJsonSmartGet],
    );

    const canUpload = Boolean(file) && uploadStatus !== 'loading';

    const uploadXlsx = async () => {
        if (!file || !canUpload) return;
        setUploadStatus('loading');
        setUploadError('');
        setImportResult(null);
        try {
            const csrf = await getCsrfToken();
            const fd = new FormData();
            fd.append('file', file);
            fd.append('mode', importMode);
            fd.append('dryRun', dryRun ? '1' : '0');
            if (importMode === 'replace') {
                const ok = window.confirm('Режим "Заменить" очистит текущую базу кодов. Продолжить?');
                if (!ok) {
                    setUploadStatus('idle');
                    return;
                }
                fd.append('confirmReplace', '1');
            }
            const resp = await fetch(`${API_BASE}${backendHint.import}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'csrf-token': csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: fd,
            });
            const data: unknown = await resp.json().catch(() => null);
            if (!resp.ok) {
                const msg = parseApiError(data) || `Ошибка загрузки: ${resp.status} ${resp.statusText}`;
                throw new Error(msg);
            }
            const parsed = parseImportResponse(data);
            if (!parsed) throw new Error('Некорректный формат ответа импорта (ожидается { ok:true, record:{...} })');
            setImportResult(parsed.record);
            setUploadStatus('success');
            const ac1 = new AbortController();
            const ac2 = new AbortController();
            void loadStats(ac1.signal);
            void loadHistory(ac2.signal);
        } catch (e) {
            setUploadStatus('error');
            setUploadError(e instanceof Error ? e.message : 'Неизвестная ошибка загрузки XLSX');
        }
    };

    const syncProducts = async () => {
        if (syncStatus === 'loading') return;
        const ok = window.confirm(
            'Запустить синхронизацию свободных кодов маркировки с товарами?\n\nЭто обновит поле products.crptAvailable (Int32) для всех товаров.',
        );
        if (!ok) return;
        setSyncStatus('loading');
        setSyncError('');
        setSyncResult(null);
        try {
            const csrf = await getCsrfToken();
            const resp = await fetch(`${API_BASE}${backendHint.syncProducts}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'csrf-token': csrf,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({}),
            });
            const data: unknown = await resp.json().catch(() => null);
            if (!resp.ok) {
                const msg = parseApiError(data) || `Ошибка синхронизации: ${resp.status} ${resp.statusText}`;
                throw new Error(msg);
            }
            const parsed = parseSyncProductsResponse(data);
            if (!parsed) throw new Error('Некорректный формат ответа синхронизации (ожидается { ok:true, updatedAt, totals:{...} })');
            setSyncResult(parsed);
            setSyncStatus('success');
            const ac = new AbortController();
            void loadStats(ac.signal);
            void loadHistory(ac.signal);
        } catch (e) {
            setSyncStatus('error');
            setSyncError(e instanceof Error ? e.message : 'Неизвестная ошибка синхронизации');
        }
    };

    // Debounced search
    useEffect(() => {
        const q = searchQuery.trim();
        if (!q) {
            setSearchStatus('idle');
            setSearchError('');
            setSearchTotal(0);
            setSearchItems([]);
            return;
        }
        setSearchStatus('loading');
        setSearchError('');
        const ac = new AbortController();
        const t = window.setTimeout(() => {
            (async () => {
                try {
                    const skip = Math.max(0, Math.trunc(searchPage)) * Math.max(1, Math.trunc(searchLimit));
                    const limit = clamp(Math.trunc(searchLimit), 5, 100);
                    const url =
                        `${API_BASE}${backendHint.search}` +
                        `?q=${encodeURIComponent(q)}` +
                        `&skip=${encodeURIComponent(String(skip))}` +
                        `&limit=${encodeURIComponent(String(limit))}`;
                    const { resp, data } = await fetchJsonSmartGet(url, ac.signal);
                    if (!resp.ok) {
                        const msg = parseApiError(data) || `Ошибка поиска: ${resp.status} ${resp.statusText}`;
                        throw new Error(msg);
                    }
                    const parsed = parseSearchResponse(data);
                    if (!parsed) throw new Error('Некорректный формат ответа поиска (ожидается { ok:true, total, items:[] })');
                    setSearchTotal(parsed.total);
                    setSearchItems(parsed.items);
                    setSearchStatus('success');
                } catch (e) {
                    if (ac.signal.aborted) return;
                    setSearchTotal(0);
                    setSearchItems([]);
                    setSearchStatus('error');
                    setSearchError(e instanceof Error ? e.message : 'Неизвестная ошибка поиска');
                }
            })();
        }, 350);
        return () => {
            ac.abort();
            window.clearTimeout(t);
        };
    }, [searchQuery, searchPage, searchLimit, backendHint.search, fetchJsonSmartGet]);

    // Initial load (stats + history)
    useEffect(() => {
        const ac = new AbortController();
        void loadStats(ac.signal);
        void loadHistory(ac.signal);
        return () => ac.abort();
    }, [loadStats, loadHistory]);

    const onRefresh = () => {
        const ac = new AbortController();
        void loadStats(ac.signal);
        void loadHistory(ac.signal);
    };

    const onCopyNormalized = async () => {
        if (!codeNormalized) return;
        try {
            await navigator.clipboard.writeText(codeNormalized);
        } catch {
            // тихо
        }
    };

    const requiredColumns = useMemo(() => ['Артикул', 'Страна', 'Штрих код', 'Код', 'Код полный', 'Код сокращенный', 'Состояние', 'Дата произв.'], []);

    const statsCards = useMemo(() => {
        const t = stats?.totals;
        return [
            { label: 'Всего кодов', value: t ? String(t.total) : '—' },
            { label: 'Доступно', value: t ? String(t.available) : '—' },
            { label: 'Зарезервировано', value: t ? String(t.reserved) : '—' },
            { label: 'Использовано', value: t ? String(t.used) : '—' },
            { label: 'Ошибочные', value: t ? String(t.invalid) : '—' },
        ];
    }, [stats]);

    const totalPages = useMemo(() => {
        const lim = Math.max(1, Math.trunc(searchLimit));
        return searchTotal > 0 ? Math.max(1, Math.ceil(searchTotal / lim)) : 1;
    }, [searchTotal, searchLimit]);

    const isBackendProbablyMissing = useMemo(() => {
        return statsHttpStatus === 404 && historyHttpStatus === 404;
    }, [statsHttpStatus, historyHttpStatus]);

    return (
        <div className="crptPage">
            <Actions title={'Честный знак'} />

            <Content>
                <div className="crptPage__content">
                    {isBackendProbablyMissing ? (
                        <div className="crptPage__callout crptPage__callout--warn">
                            Похоже, CRPT-API не доступен.
                            <div className="crptPage__mono crptPage__calloutList">
                                <div>POST {backendHint.import}</div>
                                <div>GET {backendHint.stats}</div>
                                <div>GET {backendHint.history}</div>
                                <div>GET {backendHint.searchHint}</div>
                            </div>
                        </div>
                    ) : null}

                    <div className="crptPage__grid">
                        {/* LEFT */}
                        <section className="crptPage__card crptPage__card--upload">
                            <div className="crptPage__cardHead">
                                <div className="crptPage__cardTitle">Загрузка XLSX</div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <button type="button" className="crptPage__btn crptPage__btn--ghost" onClick={onRefresh}>
                                        Обновить
                                    </button>
                                    <button
                                        type="button"
                                        className="crptPage__btn crptPage__btn--ghost"
                                        disabled={syncStatus === 'loading'}
                                        onClick={() => void syncProducts()}
                                        title="Считает свободные коды маркировки (available) по артикулу и пишет в products.crptAvailable"
                                    >
                                        {syncStatus === 'loading' ? 'Синхронизация…' : 'Синхронизировать с товарами'}
                                    </button>
                                </div>
                            </div>

                            <div
                                className={`crptPage__drop ${dragActive ? 'crptPage__drop--active' : ''}`}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openFileDialog();
                                    }
                                }}
                                onClick={() => openFileDialog()}
                                aria-label="Выбрать XLSX файл"
                                title="Кликни или перетащи сюда XLSX"
                            >
                                <input
                                    ref={fileInputRef}
                                    className="crptPage__fileInput"
                                    type="file"
                                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={onFileInputChange}
                                />
                                <div className="crptPage__dropTitle">Перетащи XLSX сюда или кликни для выбора</div>
                                <div className="crptPage__dropSub">Требуемые колонки: {requiredColumns.join(', ')}</div>
                            </div>

                            {fileMeta ? (
                                <div className="crptPage__fileMeta">
                                    <div className="crptPage__fileMetaRow">
                                        <span className="crptPage__muted">Файл:</span>
                                        <span className="crptPage__mono">{fileMeta.name}</span>
                                    </div>
                                    <div className="crptPage__fileMetaRow">
                                        <span className="crptPage__muted">Размер:</span>
                                        <span className="crptPage__mono">{fileMeta.sizeLabel}</span>
                                    </div>
                                    <div className="crptPage__fileMetaRow">
                                        <span className="crptPage__muted">Изменён:</span>
                                        <span className="crptPage__mono">{fileMeta.lastModified}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="crptPage__muted crptPage__small">Выбери отчёт XLSX.</div>
                            )}

                            <div className="crptPage__row">
                                <label className="crptPage__field">
                                    <div className="crptPage__label">Режим импорта</div>
                                    <select
                                        className="crptPage__select"
                                        value={importMode}
                                        onChange={(e) => setImportMode(e.target.value === 'replace' ? 'replace' : 'upsert')}
                                    >
                                        <option value="upsert">Добавить/обновить</option>
                                        <option value="replace">Заменить (очистить и залить заново)</option>
                                    </select>
                                </label>

                                <label className="crptPage__check">
                                    <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                                    <span>Только проверка</span>
                                </label>
                            </div>

                            <div className="crptPage__actionsRow">
                                <button type="button" className="crptPage__btn crptPage__btn--primary" disabled={!canUpload} onClick={() => void uploadXlsx()}>
                                    {uploadStatus === 'loading' ? 'Загрузка…' : dryRun ? 'Проверить файл' : 'Загрузить и обработать'}
                                </button>

                                <button
                                    type="button"
                                    className="crptPage__btn"
                                    disabled={uploadStatus === 'loading'}
                                    onClick={() => {
                                        setFile(null);
                                        setImportResult(null);
                                        setUploadError('');
                                        setUploadStatus('idle');
                                    }}
                                >
                                    Очистить
                                </button>
                            </div>

                            {uploadError ? <div className="crptPage__callout crptPage__callout--err">{uploadError}</div> : null}

                            {syncError ? <div className="crptPage__callout crptPage__callout--err">{syncError}</div> : null}
                            {syncResult ? (
                                <div className="crptPage__callout crptPage__callout--ok">
                                    <div className="crptPage__calloutTitle">Синхронизация с товарами завершена</div>
                                    <div className="crptPage__calloutGrid">
                                        <div>
                                            <span className="crptPage__muted">Дата:</span>{' '}
                                            <span className="crptPage__mono">{formatDateTimeRu(syncResult.updatedAt)}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Товаров всего:</span>{' '}
                                            <span className="crptPage__mono">{syncResult.totals.totalProducts}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Товаров с кодами (по артикулу):</span>{' '}
                                            <span className="crptPage__mono">{syncResult.totals.matchedProducts}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Уникальных артикулов в CRPT:</span>{' '}
                                            <span className="crptPage__mono">{syncResult.totals.uniqueArticles}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Свободных кодов (available):</span>{' '}
                                            <span className="crptPage__mono">{syncResult.totals.totalAvailableCodes}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Время:</span>{' '}
                                            <span className="crptPage__mono">{Math.max(0, Math.trunc(syncResult.totals.elapsedMs))} ms</span>
                                        </div>
                                    </div>
                                    <div className="crptPage__muted crptPage__small" style={{ marginTop: 8 }}>
                                        Поле <span className="crptPage__mono">products.crptAvailable</span> обновлено (Int32) для всех товаров.
                                    </div>
                                </div>
                            ) : null}

                            {importResult ? (
                                <div className="crptPage__callout crptPage__callout--ok">
                                    <div className="crptPage__calloutTitle">Готово</div>
                                    <div className="crptPage__calloutGrid">
                                        <div>
                                            <span className="crptPage__muted">Sync:</span> <span className="crptPage__mono">{importResult.syncId}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Дата:</span>{' '}
                                            <span className="crptPage__mono">{formatDateTimeRu(importResult.syncedAt)}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Статус:</span>{' '}
                                            <span className={`crptPage__badge crptPage__badge--${importResult.status}`}>{statusLabel(importResult.status)}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Файл:</span>{' '}
                                            <span className="crptPage__mono">{importResult.sourceFileName || file?.name || '—'}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Строк прочитано:</span>{' '}
                                            <span className="crptPage__mono">{importResult.rowsRead ?? '—'}</span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Импорт:</span>{' '}
                                            <span className="crptPage__mono">
                                                {importResult.imported ?? '—'}
                                                {importResult.updated != null ? `, обновлено ${importResult.updated}` : ''}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Ошибки/дубликаты:</span>{' '}
                                            <span className="crptPage__mono">
                                                {importResult.invalid ?? '—'}/{importResult.duplicates ?? '—'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="crptPage__muted">Время:</span>{' '}
                                            <span className="crptPage__mono">
                                                {importResult.elapsedMs != null ? `${Math.max(0, Math.trunc(importResult.elapsedMs))} ms` : '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            <div className="crptPage__divider" />

                            <div className="crptPage__hintBlock">
                                <div className="crptPage__hintTitle">Памятка сотруднику</div>
                                <ul className="crptPage__list">
                                    <li>
                                        Загружай отчёт <b>XLSX</b> с колонками: <span className="crptPage__mono">{requiredColumns.join(' · ')}</span>
                                    </li>
                                    <li>После импорта коды будут доступны для поиска/резервации при сборке заказа.</li>
                                    <li>
                                        Если файл большой - используй режим <b>Только проверка</b> для быстрой проверки качества данных.
                                    </li>
                                </ul>
                            </div>
                        </section>

                        {/* RIGHT */}
                        <section className="crptPage__stack">
                            <div className="crptPage__card">
                                <div className="crptPage__cardHead">
                                    <div className="crptPage__cardTitle">Сводка</div>
                                    <div className="crptPage__small crptPage__muted">{stats?.updatedAt ? `обновлено ${formatDateTimeRu(stats.updatedAt)}` : ''}</div>
                                </div>

                                {statsStatus === 'error' ? <div className="crptPage__callout crptPage__callout--err">{statsError}</div> : null}

                                <div className={`crptPage__statsGrid ${statsStatus === 'loading' ? 'crptPage__statsGrid--loading' : ''}`}>
                                    {statsCards.map((c) => (
                                        <div key={c.label} className="crptPage__statCard">
                                            <div className="crptPage__statLabel">{c.label}</div>
                                            <div className="crptPage__statValue">{c.value}</div>
                                        </div>
                                    ))}
                                </div>

                                {stats?.lastSync ? (
                                    <div className="crptPage__lastSync">
                                        <span className="crptPage__muted">Последняя синхронизация:</span>{' '}
                                        <span className="crptPage__mono">{stats.lastSync.syncId}</span>{' '}
                                        <span className="crptPage__muted">({formatDateTimeRu(stats.lastSync.syncedAt)})</span>{' '}
                                        <span className={`crptPage__badge crptPage__badge--${stats.lastSync.status}`}>{statusLabel(stats.lastSync.status)}</span>
                                    </div>
                                ) : (
                                    <div className="crptPage__muted crptPage__small">Последняя синхронизация появится после первого импорта.</div>
                                )}
                            </div>

                            <div className="crptPage__card">
                                <div className="crptPage__cardHead">
                                    <div className="crptPage__cardTitle">История загрузок</div>
                                    <div className="crptPage__small crptPage__muted">последние операции</div>
                                </div>

                                {historyStatus === 'error' ? <div className="crptPage__callout crptPage__callout--err">{historyError}</div> : null}

                                <div className={`crptPage__tableWrap ${historyStatus === 'loading' ? 'crptPage__tableWrap--loading' : ''}`}>
                                    <table className="crptPage__table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 170 }}>Дата</th>
                                                <th style={{ width: 120 }}>Статус</th>
                                                <th>Файл</th>
                                                <th style={{ width: 120 }}>Импорт</th>
                                                <th style={{ width: 120 }}>Ошибки/дубли</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="crptPage__muted crptPage__small" style={{ padding: '10px 8px' }}>
                                                        Нет данных.
                                                    </td>
                                                </tr>
                                            ) : (
                                                history.slice(0, 10).map((h) => (
                                                    <tr key={h.syncId}>
                                                        <td className="crptPage__mono">{formatDateTimeRu(h.syncedAt)}</td>
                                                        <td>
                                                            <span className={`crptPage__badge crptPage__badge--${h.status}`}>{statusLabel(h.status)}</span>
                                                        </td>
                                                        <td className="crptPage__mono">{h.sourceFileName || '—'}</td>
                                                        <td className="crptPage__mono">{h.imported ?? '—'}</td>
                                                        <td className="crptPage__mono">
                                                            {h.invalid ?? '—'}/{h.duplicates ?? '—'}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="crptPage__card">
                                <div className="crptPage__cardHead">
                                    <div className="crptPage__cardTitle">Поиск по базе маркировок</div>
                                    <div className="crptPage__small crptPage__muted">код / код полный / артикул / штрихкод</div>
                                </div>

                                <div className="crptPage__row crptPage__row--search">
                                    <input
                                        className="crptPage__input"
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setSearchPage(0);
                                        }}
                                        placeholder="Например: 010... или артикул"
                                        aria-label="Поиск"
                                    />
                                    <select
                                        className="crptPage__select"
                                        value={String(searchLimit)}
                                        onChange={(e) => {
                                            const n = Number(e.target.value);
                                            setSearchLimit(Number.isFinite(n) ? clamp(Math.trunc(n), 5, 100) : 20);
                                            setSearchPage(0);
                                        }}
                                        aria-label="Лимит"
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>

                                {searchStatus === 'error' ? <div className="crptPage__callout crptPage__callout--err">{searchError}</div> : null}

                                <div className={`crptPage__tableWrap ${searchStatus === 'loading' ? 'crptPage__tableWrap--loading' : ''}`}>
                                    <table className="crptPage__table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 120 }}>Артикул</th>
                                                <th style={{ width: 140 }}>Штрихкод</th>
                                                <th>Код (кор.)</th>
                                                <th style={{ width: 120 }}>Статус</th>
                                                <th style={{ width: 150 }}>Дата произв.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchQuery.trim() && searchItems.length === 0 && searchStatus !== 'loading' ? (
                                                <tr>
                                                    <td colSpan={5} className="crptPage__muted crptPage__small" style={{ padding: '10px 8px' }}>
                                                        Ничего не найдено.
                                                    </td>
                                                </tr>
                                            ) : null}

                                            {!searchQuery.trim() ? (
                                                <tr>
                                                    <td colSpan={5} className="crptPage__muted crptPage__small" style={{ padding: '10px 8px' }}>
                                                        Введи запрос, чтобы искать по загруженной базе.
                                                    </td>
                                                </tr>
                                            ) : null}

                                            {searchItems.map((it) => (
                                                <tr key={it.id}>
                                                    <td className="crptPage__mono">{it.article || '—'}</td>
                                                    <td className="crptPage__mono">{it.barcode || '—'}</td>
                                                    <td className="crptPage__mono" title={it.codeShort || it.codeFull || ''}>
                                                        {it.codeShort ? (it.codeShort.length > 22 ? `${it.codeShort.slice(0, 22)}…` : it.codeShort) : '—'}
                                                    </td>
                                                    <td title={it.state || ''}>{codeStatusLabel(it.status) || it.state || '—'}</td>
                                                    <td className="crptPage__mono">{it.producedAt || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="crptPage__pagination">
                                    <div className="crptPage__muted crptPage__small">
                                        {searchQuery.trim() ? (
                                            <>
                                                Найдено: <span className="crptPage__mono">{searchTotal}</span>
                                            </>
                                        ) : (
                                            <span />
                                        )}
                                    </div>

                                    <div className="crptPage__pagerBtns">
                                        <button
                                            type="button"
                                            className="crptPage__btn crptPage__btn--ghost"
                                            disabled={searchPage <= 0 || !searchQuery.trim() || searchStatus === 'loading'}
                                            onClick={() => setSearchPage((p) => Math.max(0, p - 1))}
                                        >
                                            Назад
                                        </button>
                                        <div className="crptPage__mono crptPage__small">{searchQuery.trim() ? `${searchPage + 1}/${totalPages}` : '—'}</div>
                                        <button
                                            type="button"
                                            className="crptPage__btn crptPage__btn--ghost"
                                            disabled={!searchQuery.trim() || searchStatus === 'loading' || searchTotal <= 0 || searchPage + 1 >= totalPages}
                                            onClick={() => setSearchPage((p) => p + 1)}
                                        >
                                            Вперёд
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="crptPage__card">
                                <div className="crptPage__cardHead">
                                    <div className="crptPage__cardTitle">Инструмент: проверка кода</div>
                                    <div className="crptPage__small crptPage__muted">нормализация скана и быстрая проверка</div>
                                </div>

                                <div className="crptPage__row crptPage__row--tool">
                                    <input
                                        className="crptPage__input"
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value)}
                                        placeholder="Вставь/отсканируй код (может начинаться с ]d2)"
                                        aria-label="Код"
                                    />
                                    <button type="button" className="crptPage__btn" disabled={!codeNormalized} onClick={() => void onCopyNormalized()}>
                                        Копировать
                                    </button>
                                </div>

                                <div className="crptPage__toolResult">
                                    <div>
                                        <span className="crptPage__muted">Нормализовано:</span>{' '}
                                        <span className="crptPage__mono">{codeNormalized ? codeNormalized : '—'}</span>
                                    </div>
                                    <div>
                                        <span className="crptPage__muted">Похоже на ЧЗ:</span>{' '}
                                        <span
                                            className={`crptPage__badge ${
                                                codeNormalized ? (codeIsOk ? 'crptPage__badge--success' : 'crptPage__badge--failed') : ''
                                            }`}
                                        >
                                            {codeNormalized ? (codeIsOk ? 'Да' : 'Нет') : '—'}
                                        </span>
                                    </div>
                                </div>

                                <div className="crptPage__muted crptPage__small">
                                    Примечание: это заготовка. Строгую валидацию сделаем после тестирования при импорте/резервации.
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </Content>
        </div>
    );
};

export default CrptPage;
